/**
 * TEMPORARY FIX: This client-side trade-in mapping logic is a temporary solution.
 * In follow-up PRs, this mapping will be moved to the server side for better
 * consistency and maintainability.
 */

import type { ITradeIn as IAccelerateTradeIn, ITradeIn } from '@makemydeal/dr-platform-types';
import { getTradeInSummary, transformTradeInVehicleOptions } from './tradeInUtils';
import type { VehicleCode } from './types';
import { TRADE_OWNERSHIP_TYPE, TradeVehicleCodeType } from './types';

export const isTradeIncludedInPayment = (tradeIn: any): boolean => {
    return tradeIn?.isTradeIncluded || false;
};

export const getOwnershipType = (vehicle: any): string => {
    return vehicle.isLeaseTrade ? TRADE_OWNERSHIP_TYPE.LEASE : TRADE_OWNERSHIP_TYPE.PURCHASE;
};

export const getOwnershipTypeFromTradeInType = (tradeInType: IAccelerateTradeIn['tradeInType']) => {
    if (tradeInType === 'Lease') {
        return TRADE_OWNERSHIP_TYPE.LEASE;
    }
    return TRADE_OWNERSHIP_TYPE.PURCHASE;
};

export const getTradeInValuation = (tradeIn: any): any => {
    return tradeIn?.tradeValuation || {};
};

export const getAlgCode = (vehicleCodes: VehicleCode[]) => {
    const emptyAlgCode: VehicleCode = {
        codeType: TradeVehicleCodeType.ALG,
        makeCode: undefined,
        styleCode: undefined
    };

    return vehicleCodes?.find((code) => code.codeType === TradeVehicleCodeType.ALG) || emptyAlgCode;
};

/**
 * Maps DealXG (Deal Exchange) trade-in data to Accelerate's ITradeIn format.
 * This mapping logic is based on the structure provided by the DealXG API.
 *
 * @param dealExchangeRecord - The deal exchange record containing trade-in vehicle information
 * @param enableDXGTradeInTypeField - Flag to enable DXG-specific trade-in type field mapping
 * @returns ITradeIn object compatible with Accelerate applications
 */
export const mapToTradeIn = (dealExchangeRecord: any, enableDXGTradeInTypeField = false): ITradeIn => {
    const response: ITradeIn = {
        amountOwed: 0, // 'tradeInSummary.totalPayoff || 0', // need to review is this value is correct in DXG data schema.
        bodyStyle: '',
        owed: 0, // 'tradeInSummary.totalPayoff || 0',
        color: '',
        condition: '',
        driveLine: '',
        engine: '',
        expirationDate: '',
        goodThroughDate: '',
        interiorColor: '',
        isBuyerRegisteredOwner: false,
        isSellingVehicleRegisteredInSameState: false,
        isAmountAppliedToFinancing: false,
        isCompleted: false,
        lienHolder: '',
        make: '',
        mileage: 0,

        model: '',

        modelId: '',
        offerDate: '', // need to review where is this value in DXG data schema.
        offerId: '', // need to review where is this value in DXG data schema.

        ownershipType: '',
        perDiemInterest: 0,
        source: 'USER', // To allow offer validation to pass

        shouldIncludeTradeIn: false,

        transmission: '',
        trim: '',
        trimId: '', // need to review where is this value in DXG data schema.

        value: 0, // 'tradeInSummary.totalAllowance' || 0,
        vehicleId: '',

        vin: '',

        year: 2020, // 'vehicle.year', should be a number
        vehicleOptions: {},
        makeId: null
    };

    if (!dealExchangeRecord?.dealStructure?.vehicles?.tradeInVehicles) {
        return {} as ITradeIn;
    }

    try {
        // just process 1st trade-in, Accelerate does not support multiple trade-ins
        const { tradeIns } = dealExchangeRecord.dealStructure.vehicles.tradeInVehicles;
        const tradeInSummary = getTradeInSummary(dealExchangeRecord);
        const tradeIn = tradeIns[0] || {};
        const vehicle = tradeIn.tradeVehicle || {};
        const vehicleCodes = vehicle.vehicleCodes || [];
        const algCode = getAlgCode(vehicleCodes);
        const valuation = getTradeInValuation(tradeIn);
        const expDate = valuation.acquisitionOffer?.expirationDate;
        const acv = valuation.actualCashValue;
        const expDateString = expDate ? new Date(expDate).toISOString() : '';
        const { tradePayoffDetails = {} } = tradeIn;

        response.acv = acv || 0;
        response.amountOwed = tradeInSummary.totalPayoff || 0;
        response.amountOwedSource = tradePayoffDetails.tradeInPayoffSource;
        response.bodyStyle = vehicle.bodyStyle || '';
        response.owed = tradeInSummary.totalPayoff || 0;
        response.color = vehicle.exteriorColor;
        response.condition = vehicle.condition || '';
        response.driveLine = vehicle.powertrain?.drivetrainType || '';
        response.engine = vehicle.powertrain?.engineDetails?.description || '';
        response.expirationDate = expDateString;
        response.goodThroughDate = tradePayoffDetails.goodThroughDate || '';
        response.interiorColor = vehicle.interiorColor || '';
        response.isBuyerRegisteredOwner = tradeIn.isBuyerRegisteredOwner ?? true;
        response.isSellingVehicleRegisteredInSameState = tradeIn.isSellingVehicleRegisteredInSameState ?? true;
        response.isAmountAppliedToFinancing = isTradeIncludedInPayment(tradeIn);
        response.isCompleted = true;
        response.lienHolder = tradePayoffDetails.lienHolder || '';
        response.make = vehicle.make || '';
        response.makeId = `${algCode.makeCode || ''}`;
        response.mileage = vehicle.odometer || 1;

        response.lienHolderAccountNumber = tradePayoffDetails.lienHolderAccountNumber;
        response.lienHolderPhoneNumber = tradePayoffDetails.lienHolderPhoneNumber || '';
        response.lienHolderAddress = {
            address: tradePayoffDetails.lienHolderAddress?.addressLine1,
            city: tradePayoffDetails.lienHolderAddress?.city,
            stateProvince: tradePayoffDetails.lienHolderAddress?.stateProvince,
            postalCode: tradePayoffDetails.lienHolderAddress?.postalCode
        };
        response.zip = tradePayoffDetails.lienHolderAddress?.postalCode || '';

        response.model = vehicle.model || '';

        response.modelId = vehicle.modelCode || '';
        response.offerDate = valuation.acquisitionOffer?.offerDate || new Date().toISOString();
        response.offerId = valuation.acquisitionOffer?.offerId || '';
        response.ownershipType = enableDXGTradeInTypeField
            ? getOwnershipTypeFromTradeInType(tradeIn.tradeInType)
            : getOwnershipType(vehicle);
        response.perDiemInterest = tradeIn.tradePayoffDetails?.perDiemInterest || 0;
        response.source = valuation.acquisitionOffer?.source || 'SD';
        response.shouldIncludeTradeIn = isTradeIncludedInPayment(tradeIn);

        response.transmission = vehicle.powertrain?.transmissionDetails?.description || '';
        response.trim = vehicle.trim || '';
        response.trimId = `${algCode.styleCode || ''}`;

        response.value = tradeInSummary.totalAllowance;
        response.vehicleId = '';

        response.vin = vehicle.vin || '';

        response.year = !isNaN(vehicle.year) ? parseInt(vehicle.year, 10) : 0;
        response.vehicleOptions = transformTradeInVehicleOptions(vehicle.installedOptions || {});

        // optional trade tax reduction fields on dxg
        response.totalTradeInTaxReduction = tradeInSummary.totalTradeInTaxReduction;
        response.tradeInTaxReduction = tradeIn.tradeInTaxReduction;

        return response;
    } catch (error) {
        return {} as ITradeIn;
    }
};
